<template>
  <ContentfulHeroSections :item="personalizedPage" :toc-info="tocInfo" />

  <template v-if="isAccountArea">
    <LibGroupedSections
      v-if="item.fields.contentSection"
      :entries="item.fields.contentSection"
    />
    <LibGroupedSections
      v-if="item.fields.callToAction"
      :entries="[item.fields.callToAction]"
      :as-section="true"
    />
  </template>
  <template v-else>
    <ArticleTocLayout
      v-if="personalizedPage.fields.contentSection && showTocValue"
      :key="`toc-${personalizedPage.sys.id}-${personalizedPage.sys.updatedAt}`"
      :mobile-title="$textDictionary['toc.mobileTitle'] ?? 'Table of content'"
      :aside-aria-label="
        $textDictionary['toc.asideAriaLabel'] ?? 'Table of content'
      "
      :nav-heading="$textDictionary['toc.navHeading'] ?? 'Content of page'"
      :open-toc-btn-action-label="
        $textDictionary['toc.openTocBtnActionLabel'] ?? 'open'
      "
      :prev-btn-label="$textDictionary['toc.prevBtnLabel'] ?? 'previous'"
      :next-btn-label="$textDictionary['toc.nextBtnLabel'] ?? 'next'"
      :toc-skip-btn-text="
        $textDictionary['toc.tocSkipBtnText'] ?? 'Skip Table of content'
      "
      :toc-items="tocitems"
    >
      <LibGroupedSections :entries="personalizedPage.fields.contentSection" />
    </ArticleTocLayout>
    <LibGroupedSections
      v-if="personalizedPage.fields.contentSection && !showTocValue"
      :entries="personalizedPage.fields.contentSection"
    />

    <LibGroupedSections
      v-if="personalizedPage.fields.callToAction"
      :entries="[personalizedPage.fields.callToAction]"
      :as-section="true"
    />
  </template>
  <PaywallPreviewToggle v-if="showPaywallPreviewToggle" />
</template>

<script setup lang="ts">
import type { IContentPage, LOCALE_CODE } from '~~/lib/ContentfulService';
import {
  CONTENT_PAGE_TITLE,
  TOC_INFO,
  isAccountAreaPage
} from '~~/lib/constants';
import { ArticleTocLayout } from '@hypercodestudio/basler-components';
import type { TOCInfo } from '~/types/TocType';
import { mapToFormattedDate } from '~/utils/mapper/mapToFormattedDate';
import { useHostname } from '~/composables/useHostname';
import { containsPaywall } from '~/utils/paywall/containsPaywall';
import { useGlobalH2Index } from '~/composables/useGlobalH2Index';
import { getPaywall } from '~/utils/paywall/getPaywall';
import { isLeadPaywall } from '~/utils/paywall/isLeadPaywall';
import { isPreviewFlagEnabled } from '~/utils/paywall/resolvePaywallVisibility';
import PaywallPreviewToggle from '~/components/global/lib/PaywallPreviewToggle.vue';
import { submitLoggedInPaywallUnlock } from '~/utils/paywall/paywallFormhandlerCapture';
import { useGeoinformationStore } from '~/stores/geoinformationStore/geoinformationStore';
import { getLocaleCountryLabel } from '~/stores/geoinformationStore/helper/getLocaleCountryLabel';
import { mapShopStoreToSalesRegion } from '~/utils/shop/mapShopStoreToSalesRegion';
import { extractIso31662Subdivision } from '~/utils/geolocation/extractIso31662Subdivision';
import type { PageTeaserMeta } from '~/types/PageTeaserMeta';
import { extractPageTeaserMeta } from '~~/lib/ContentfulTransformerService/helper/extractPageTeaserMeta';

const {
  $textDictionary,
  $globalPageSettings,
  $analytics,
  $shopStore,
  $activeLocale
} = useNuxtApp();
const serverUrlOrigin = useHostname();

interface Props {
  item: IContentPage;
  /** Present only when the page has `showToc` enabled. */
  tocitems?: TocItem[];
  /** From `/api/contentful`; only passed when a hero article has `showReadingTime`. */
  readingTime?: number;
  /** Pre-computed root page teaser fields (stripped from `item.fields`). */
  pageTeaserMeta?: PageTeaserMeta;
}
const props = defineProps<Props>();

const pageTeaserMeta = computed(
  () => props.pageTeaserMeta ?? extractPageTeaserMeta(props.item)
);
const contentPageTitle = computed(
  () => pageTeaserMeta.value?.title ?? props.item.fields.seoTitle ?? ''
);

provide(CONTENT_PAGE_TITLE, contentPageTitle);

const { loggedIn, user } = useShopAuth();

const PAGETYPE_TO_ARTICLETYPE: Record<string, string> = {
  'Knowledge Article': 'TechArticle',
  Tutorial: 'TechArticle',
  Product: 'WebPage',
  News: 'NewsArticle',
  'Use Case': 'TechArticle',
  'Customer Story	': 'WebPage',
  Miscellaneous: 'WebPage'
};

const isAccountArea = computed(() => isAccountAreaPage(props.item));
// Follow props: after paywall unlock the page is refetched in place and must
// re-render the full contentSection (legacy name from removed Ninetailed wiring).
const personalizedPage = computed(() => props.item);

const showTocValue = toRef(() => props.item?.fields?.showToc);

// Reset global H2 index when page changes to ensure consistent IDs
const { reset: resetH2Index } = useGlobalH2Index();
watch(
  () => props.item.sys.id,
  () => {
    resetH2Index();
  },
  { immediate: true }
);

const tocInfo = computed<TOCInfo>(() => {
  return {
    showToc: showTocValue.value,
    centeredText: !showTocValue.value,
    date: props.item.fields?.date
      ? mapToFormattedDate(
          props.item.fields?.date.toString(),
          $activeLocale.value.webapp as LOCALE_CODE
        )
      : undefined,
    readingTime: props.readingTime
  };
});
provide(TOC_INFO, tocInfo);

const url = computed(() =>
  buildUrlString(
    $activeLocale.value.webapp,
    undefined,
    undefined,
    undefined,
    serverUrlOrigin
  )
);

const { isValidBot, isPaywallVisible, paywallPreviewMode } = usePaywall();
const hasPaywall = containsPaywall(props.item);
const {
  public: { isPreviewEnabled }
} = useRuntimeConfig();
const showPaywallPreviewToggle = computed(
  () =>
    isPreviewFlagEnabled(isPreviewEnabled) &&
    containsPaywall(props.item) &&
    !isLeadPaywall(getPaywall(props.item))
);
const geoinformationStore = useGeoinformationStore();
const requestURL = useRequestURL();
const logger = useLogger();

// Geo detection as primary source, locale code as fallback (e.g. on localhost)
// Country name intentionally in German for Pardot
const resolvedCountry = computed(() => {
  const geoCode = geoinformationStore.detectedCountry;
  if (geoCode) {
    return getLocaleCountryLabel(geoCode, 'de') ?? geoCode;
  }
  const localeCode = $activeLocale.value?.webapp
    .split('-')
    .at(-1)
    ?.toUpperCase();
  return getLocaleCountryLabel(localeCode, 'de') ?? localeCode;
});

onMounted(() => {
  if (!hasPaywall || isValidBot.value) {
    return;
  }

  const previewMode = paywallPreviewMode.value;

  if (!previewMode) {
    $analytics.pushToDataLayer({
      event: 'gated_content',
      event_action: isPaywallVisible.value
        ? 'view_gated_content_c'
        : 'view_gated_content_paid_c'
    });
  }

  if (!loggedIn.value || previewMode === 'locked') {
    return;
  }

  const paywall = getPaywall(props.item);
  if (!paywall?.fields?.formHandlerUrl || isLeadPaywall(paywall)) {
    return;
  }

  const companyClaim = user.value?.claims?.Company;
  const firstName =
    typeof user.value?.userInfo?.given_name === 'string'
      ? user.value.userInfo.given_name
      : undefined;
  const lastName =
    typeof user.value?.userInfo?.family_name === 'string'
      ? user.value.userInfo.family_name
      : undefined;
  const email =
    typeof user.value?.userInfo?.email === 'string'
      ? user.value.userInfo.email
      : undefined;
  const baslerId = user.value?.claims?.oid
    ? String(user.value.claims.oid)
    : undefined;

  // Await geo init so region/country are available before one-shot capture
  void (async () => {
    try {
      if (!geoinformationStore.initiated) {
        await geoinformationStore.init();
      }
    } catch (err: unknown) {
      logger.warn(
        '[ContentPage] geolocation init failed before paywall capture',
        err
      );
    }

    try {
      const submitted = await submitLoggedInPaywallUnlock({
        locale: String($activeLocale.value.webapp ?? ''),
        entryId: paywall.sys.id,
        firstname: firstName,
        lastname: lastName,
        email,
        company: typeof companyClaim === 'string' ? companyClaim : undefined,
        baslerId,
        filename: requestURL.href,
        country: resolvedCountry.value,
        salesRegion: mapShopStoreToSalesRegion(String($shopStore.value ?? '')),
        territory: extractIso31662Subdivision(
          geoinformationStore.detectedRegion
        )
      });

      if (!submitted) {
        return;
      }

      $analytics.pushToDataLayer({
        event: 'formSent',
        goalPage: '/downloadform/sent',
        page_location: requestURL.href
      });
    } catch (err: unknown) {
      // User already sees content; retry on next visit if capture was not marked
      logger.warn(
        '[ContentPage] logged-in paywall formhandler capture failed',
        err
      );
    }
  })();
});

const accessibility = computed(() =>
  containsPaywall(props.item) && !isLeadPaywall(getPaywall(props.item))
    ? {
        isAccessibleForFree: false,
        hasPart: {
          '@type': 'WebPageElement',
          isAccessibleForFree: false,
          cssSelector: '.paywall-content'
        }
      }
    : { isAccessibleForFree: true }
);

const schema = computed(() => ({
  '@context': 'https://schema.org',
  '@type': props.item.fields.format
    ? PAGETYPE_TO_ARTICLETYPE[props.item.fields.format]
    : 'WebPage',
  ...(props.item.fields.date && {
    datePublished: new Date(props.item.fields.date).toISOString(),
    dateModified: new Date(props.item.fields.date).toISOString()
  }),
  headline: pageTeaserMeta.value?.title,
  description: pageTeaserMeta.value?.description,
  image: pageTeaserMeta.value?.imageUrl,
  author: [
    {
      '@type': 'Organization',
      name: $globalPageSettings.value?.companyName ?? undefined,
      url: url.value,
      logo: $globalPageSettings.value?.companyLogo?.url ?? undefined,
      address: {
        '@type': 'PostalAddress',
        streetAddress: $globalPageSettings.value?.companyStreet ?? undefined,
        postalCode: $globalPageSettings.value?.companyPostalCode ?? undefined,
        addressLocality: $globalPageSettings.value?.companyCity ?? undefined,
        addressRegion: $globalPageSettings.value?.companyRegion ?? undefined,
        addressCountry:
          $activeLocale.value?.webapp.split('-').at(-1)?.toUpperCase() ??
          undefined
      }
    }
  ],
  ...accessibility.value
}));

// Force reactivity for TOC rendering on navigation
watch(
  () => [props.item.sys.id, props.item.fields.showToc],
  () => {
    // Force re-evaluation of tocInfo when item changes
    nextTick(() => {
      // This ensures the component properly re-renders on navigation
    });
  },
  { immediate: true, deep: true }
);

useHead({
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify(schema.value),
      tagPriority: -1 // Optional: Sicherstellen, dass es zuletzt eingefügt wird
    }
  ]
});
</script>
